import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ShieldAlert, ShieldCheck } from 'lucide-react';
import { queryKeys } from '@vtak/shared';
import { useWorkspaceStore } from '@/stores/workspace-store';
import { useSidebarStore } from '@/stores/sidebar-store';
import { workspaceClient } from '@/services/workspace-client';
import { resolveEmblem } from '@/constants/emblems';
import { ConfigSourceTag, LocationJumpList } from '@/components/map/workspace-hud-parts';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { PendingList } from './pending-list';
import { MemberList } from './member-list';
import { InviteMint } from './invite-mint';
import { LaunchFocusEditor } from './launch-focus-editor';
import { WorkspaceSettingsEditor } from './workspace-settings-editor';
import { AreaAssignEditor } from './area-assign-editor';
import { MissionAreasEditor } from './mission-areas-editor';
import { UnitGuests } from './unit-guests';
import { fetchJson } from '@/services/api-client';
import type { EchoGuest } from '@vtak/shared';

/**
 * Unit panel: identity (emblem, names, org path, status) and location
 * quick-jumps for every member, plus the admin console (approve requests,
 * view members, mint invites) for unit admins.
 */
export function WorkspaceAdminPanel(): React.JSX.Element {
  const workspace = useWorkspaceStore((s) => s.workspace);
  const role = useWorkspaceStore((s) => s.role);
  const platformAdmin = useWorkspaceStore((s) => s.platformAdmin);
  const isAdmin = role === 'admin' || platformAdmin;

  if (!workspace) {
    return <Notice text="No active unit." />;
  }

  return (
    <div className="flex flex-1 flex-col overflow-hidden">
      <IdentitySection />
      <SectionLabel text="Locations" />
      <LocationJumpList
        unitId={workspace.unitId}
        onJump={() => useSidebarStore.getState().setActivePanel(null)}
      />
      {isAdmin && (
        <>
          <SectionLabel text="Administration" />
          <AdminTabs unitId={workspace.unitId} />
        </>
      )}
    </div>
  );
}

/** Identity header: emblem, unit names, org breadcrumb, role + config source. */
function IdentitySection(): React.JSX.Element | null {
  const workspace = useWorkspaceStore((s) => s.workspace);
  const role = useWorkspaceStore((s) => s.role);
  const emblem = useWorkspaceStore((s) => s.emblem);
  const orgPath = useWorkspaceStore((s) => s.orgPath);
  const configSource = useWorkspaceStore((s) => s.configSource);

  if (!workspace) return null;
  const emblemUrl = resolveEmblem(emblem);

  return (
    <div className="flex items-center gap-3 border-b border-echo-border px-3 py-3">
      {emblemUrl ? (
        <img
          src={emblemUrl}
          alt={`${workspace.shortName} emblem`}
          className="h-11 w-11 shrink-0 rounded-md object-contain ring-1 ring-echo-border"
        />
      ) : (
        <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-md bg-echo-border/40 ring-1 ring-echo-border">
          <ShieldCheck className="h-5 w-5 text-echo-yellow" />
        </span>
      )}
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="font-chakra text-sm font-bold uppercase tracking-wider text-foreground">
            {workspace.shortName}
          </span>
          {role && (
            <span className="rounded bg-echo-yellow/20 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-widest text-echo-yellow">
              {role}
            </span>
          )}
        </div>
        <p className="truncate text-[11px] text-muted-foreground">{workspace.unitName}</p>
        {orgPath.length > 0 && (
          <p className="truncate text-[10px] font-medium text-muted-foreground/80">
            {orgPath.join(' › ')}
          </p>
        )}
      </div>
      <ConfigSourceTag configSource={configSource} />
    </div>
  );
}

/** Admin console tabs (pending approvals, members, invites). */
function AdminTabs({ unitId }: { unitId: string }): React.JSX.Element {
  const [tab, setTab] = useState('pending');

  return (
    <Tabs value={tab} onValueChange={setTab} className="flex-1">
      <TabsList>
        <TabsTrigger value="pending">
          Pending <PendingBadge unitId={unitId} />
        </TabsTrigger>
        <TabsTrigger value="members">Members</TabsTrigger>
        <TabsTrigger value="invite">Invite</TabsTrigger>
        <TabsTrigger value="guests">
          Guests <GuestBadge unitId={unitId} />
        </TabsTrigger>
        <TabsTrigger value="focus">Focus</TabsTrigger>
        <TabsTrigger value="settings">Settings</TabsTrigger>
        <TabsTrigger value="areas">Areas</TabsTrigger>
        <TabsTrigger value="assign">Assign</TabsTrigger>
      </TabsList>
      <TabsContent value="pending" className="flex flex-col">
        <PendingList unitId={unitId} />
      </TabsContent>
      <TabsContent value="members" className="flex flex-col">
        <MemberList unitId={unitId} />
      </TabsContent>
      <TabsContent value="invite" className="flex flex-col">
        <InviteMint unitId={unitId} />
      </TabsContent>
      <TabsContent value="guests" className="flex flex-col">
        <UnitGuests unitId={unitId} />
      </TabsContent>
      <TabsContent value="focus" className="flex flex-col">
        <LaunchFocusEditor unitId={unitId} />
      </TabsContent>
      <TabsContent value="settings" className="flex flex-col">
        <WorkspaceSettingsEditor unitId={unitId} />
      </TabsContent>
      <TabsContent value="areas" className="flex flex-col">
        <MissionAreasEditor unitId={unitId} />
      </TabsContent>
      <TabsContent value="assign" className="flex flex-col">
        <AreaAssignEditor unitId={unitId} />
      </TabsContent>
    </Tabs>
  );
}

/** Chakra eyebrow label separating panel sections */
function SectionLabel({ text }: { text: string }): React.JSX.Element {
  return (
    <p className="border-b border-echo-border/50 px-3 pb-1.5 pt-3 font-chakra text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
      {text}
    </p>
  );
}

/** Count badge on the Pending tab. */
function PendingBadge({ unitId }: { unitId: string }): React.JSX.Element | null {
  const query = useQuery({
    queryKey: [...queryKeys.workspace.members(unitId), 'pending'],
    queryFn: () => workspaceClient.getMembers(unitId, 'pending'),
  });
  const count = query.data?.length ?? 0;
  if (count === 0) return null;
  return (
    <span className="ml-1 inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-echo-yellow px-1 text-[9px] font-bold text-black">
      {count}
    </span>
  );
}

/** Count badge on the Guests tab (pending guests awaiting approval). */
function GuestBadge({ unitId }: { unitId: string }): React.JSX.Element | null {
  const query = useQuery({
    queryKey: ['unit', 'guests', unitId, 'badge'],
    refetchInterval: 15_000,
    queryFn: () => fetchJson<EchoGuest[]>(`/api/units/${encodeURIComponent(unitId)}/guests`, []),
  });
  const count = (query.data ?? []).filter((g) => g.status === 'pending').length;
  if (count === 0) return null;
  return (
    <span className="ml-1 inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-echo-yellow px-1 text-[9px] font-bold text-black">
      {count}
    </span>
  );
}

/** Centered muted notice with an optional shield icon. */
function Notice({ text, icon }: { text: string; icon?: boolean }): React.JSX.Element {
  return (
    <div className="flex flex-1 flex-col items-center justify-center gap-2 p-6 text-center">
      {icon && <ShieldAlert className="h-6 w-6 text-muted-foreground" />}
      <p className="text-xs text-muted-foreground">{text}</p>
    </div>
  );
}
